import {
  usePostApiV5AiMessagesGenerate,
  usePostApiV5AiMessagesGenerateReply,
} from '@liscio/api';
import type {
  AiMessageGenerateResponse,
  AiMessageGenerateReplyResponse,
} from '@liscio/api';

export interface UseGenerateAiMessageOptions {
  onSuccess?: (response: AiMessageGenerateResponse) => void;
  onError?: (error: Error) => void;
  onGuardrail?: (message: string) => void;
}

const getAiErrorField = (
  error: any,
  field: 'error' | 'error_explanation',
): string | undefined =>
  error?.[field] || error?.data?.[field] || error?.response?.data?.[field];

/**
 * Hook for generating AI message content
 * Provides mutation functionality with proper error handling and user feedback
 */
export const useGenerateAiMessage = (
  options: UseGenerateAiMessageOptions = {},
) =>
  usePostApiV5AiMessagesGenerate({
    mutation: {
      onSuccess: (response: AiMessageGenerateResponse) => {
        // Handle successful generation
        if (response.success && response.draft) {
          options.onSuccess?.(response);
        } else {
          const errorMessage = 'No content was generated. Please try again.';
          options.onError?.(new Error(errorMessage));
        }
      },
      onError: (error: any) => {
        // Extract user-friendly error message from error response
        const errorMessage =
          getAiErrorField(error, 'error_explanation') ||
          error?.message ||
          'An error occurred while generating content.';

        // Check if it's a guardrail error
        if (getAiErrorField(error, 'error') === 'guardrail_triggered') {
          const guardrailMessage =
            getAiErrorField(error, 'error_explanation') ||
            'AI content generation blocked due to security concerns.';

          options.onGuardrail?.(guardrailMessage);
          return;
        }

        options.onError?.(new Error(errorMessage));
      },
    },
  });

/**
 * Hook for generating AI reply content based on thread history
 * Provides mutation functionality with proper error handling and user feedback
 */
export const useGenerateAiReply = (options: UseGenerateAiMessageOptions = {}) =>
  usePostApiV5AiMessagesGenerateReply({
    mutation: {
      onSuccess: (response: AiMessageGenerateReplyResponse) => {
        // Handle successful generation
        if (response.success && response.draft) {
          // Convert reply response to the expected format for the callback
          const compatibleResponse: AiMessageGenerateResponse = {
            success: response.success,
            draft: response.draft,
            subject_line: '', // Reply doesn't have subject line
            ai_message_draft_uuid: response.ai_message_draft_uuid ?? null,
          };
          options.onSuccess?.(compatibleResponse);
        } else {
          const errorMessage = 'No reply was generated. Please try again.';
          options.onError?.(new Error(errorMessage));
        }
      },
      onError: (error: any) => {
        // Extract user-friendly error message from error response
        const errorMessage =
          getAiErrorField(error, 'error_explanation') ||
          error?.message ||
          'An error occurred while generating reply.';

        // Check if it's a guardrail error
        if (getAiErrorField(error, 'error') === 'guardrail_triggered') {
          const guardrailMessage =
            getAiErrorField(error, 'error_explanation') ||
            'AI content generation blocked due to security concerns.';

          options.onGuardrail?.(guardrailMessage);
          return;
        }

        options.onError?.(new Error(errorMessage));
      },
    },
  });
